import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import type { Toast } from "primereact/toast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import AppToast from "../components/AppToast";
import ChallengePicker from "../components/ChallengePicker";
import Countdown from "../components/Countdown";
import { usePageTitle } from "../components/PageTitle";
import { useLiveRefresh } from "../hooks/useLiveRefresh";
import { SOCKET_EVENTS } from "../lib/socket";
import StatusPill from "../components/StatusPill";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/apiError";
import { clearToken, getToken, isTokenValid } from "../lib/auth";
import { applyDirection } from "../lib/direction";
import { isWelcomeChallenge, pinWelcomeFirst } from "../lib/welcome";
import type { AdminChallengeDto, ChallengeContent } from "../types/api";
type ScoreRow = {
  rank: number;
  team_id: string;
  team_name: string;
  points: number;
  solves: number;
  last_solve: string | null;
};
type PlayerRow = { id: string; name: string };
type TeamRow = {
  id: string;
  name: string;
  join_link: string;
  players?: PlayerRow[];
};
type TeamTableRow = {
  rowType: "team" | "players";
  rowId: string; // unique per row
  team: TeamRow;
};
type GameRow = {
  id: string;
  code: string;
  status: "lobby" | "active" | "ended";
  max_team_size: number;
  duration_seconds?: number;
  started_at?: string | null;
  ends_at?: string | null;
};
type Challenge = {
  id: string;
  key: string;
  type: string;
  difficulty: string;
  points: number;
  order_index: number;
  category: string;
  title?: string;
  content?: ChallengeContent;
};
export default function AdminDashboard({ gameCode }: { gameCode: string }) {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    applyDirection(i18n.language);
  }, [i18n.language]);

  usePageTitle(t("app.admin"));
  const nav = useNavigate();
  /*
   * Checked on every render, but the bail-out happens *after* every hook has
   * run (see the guard just above the JSX). Returning here would change the
   * number of hooks between renders, and React crashes the page with
   * "rendered fewer hooks than expected" the moment a token expires in an
   * open tab: a white screen instead of a redirect to the login form.
   */
  const tokenOk = isTokenValid(getToken());
  // --- games state ---
  const [games, setGames] = useState<GameRow[]>([]);
  const [activeGame, setActiveGame] = useState<string>(gameCode);

  const selectedGame = games.find((g) => g.code === activeGame);
  const canStart = !!activeGame && selectedGame?.status === "lobby";
  const canExtend = !!activeGame && selectedGame?.status === "active";

  // --- timer ---
  const [newDurationMin, setNewDurationMin] = useState<number>(30);
  const [extendSec, setExtendSec] = useState<number>(10);
  const [adminStatus, setAdminStatus] = useState<"lobby" | "active" | "ended">(
    "lobby"
  );
  const offsetRef = useRef(0);
  const endsAtRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  // --- score states ---
  /* Live scores and team changes arrive over the socket instead of a poll. */
  const { revision, connected: liveConnected } = useLiveRefresh({
    gameCode: activeGame,
  });
  /* Roster changes are a separate event: a player joining moves no points,
     but it does have to appear in the teams table straight away. */
  const { revision: teamsRevision } = useLiveRefresh({
    gameCode: activeGame,
    events: [SOCKET_EVENTS.teamsUpdated],
  });
  const [scores, setScores] = useState<ScoreRow[]>([]);
  const [scoreLoading, setScoreLoading] = useState(false);

  // create game form
  const [newCode, setNewCode] = useState<string>("");
  const [newMaxSize, setNewMaxSize] = useState<number>(4);

  // --- toast ---
  const activeToastsRef = useRef<Map<string, number>>(new Map());
  // --- challenges ---
  const [allChallenges, setAllChallenges] = useState<Challenge[]>([]);
  const [available, setAvailable] = useState<Challenge[]>([]);
  const [selected, setSelected] = useState<Challenge[]>([]);

  // category filter
  const [categoryFilter, setCategoryFilter] = useState<string>("__all__");

  // unique categories from loaded challenges
  const categories = Array.from(
    new Set(allChallenges.map((c) => c.category))
  ).sort();

  // filter the source list (Available) by category, but keep hidden items around
  const filteredAvailable =
    categoryFilter === "__all__"
      ? available
      : available.filter((c) => c.category === categoryFilter);


  // teams state
  const [count, setCount] = useState<number>(4);
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const tableRows = useMemo<TeamTableRow[]>(() => {
    const rows: TeamTableRow[] = [];
    for (const t of teams) {
      rows.push({ rowType: "team", rowId: `team-${t.id}`, team: t });
      rows.push({ rowType: "players", rowId: `players-${t.id}`, team: t });
    }
    return rows;
  }, [teams]);
  const [loading, setLoading] = useState(false);
  const toast = useRef<Toast>(null);

  function showError(detail: string, summary = t("adminToasts.error")) {
    showToastOnce({ severity: "error", summary, detail, life: 4000 });
  }
  function showSuccess(detail: string, summary = t("adminToasts.success")) {
    showToastOnce({ severity: "success", summary, detail, life: 2500 });
  }
  function onLogout() {
    clearToken();
    nav("/admin/login", { replace: true });
  }

  // ---- API helpers ----
  /* The clock lives in <Countdown>, which ticks itself. Here we only keep the
     server offset and end time in refs, so nothing re-renders on a timer. */
  const getRemaining = useCallback(() => {
    if (!endsAtRef.current) return null;
    return Math.max(0, endsAtRef.current - (Date.now() + offsetRef.current));
  }, []);

  /* A token that expires mid-session should return to the login form rather
     than leave a dashboard on screen that can no longer talk to the API. */
  useEffect(() => {
    if (!tokenOk) nav("/admin/login", { replace: true });
  }, [tokenOk, nav]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!activeGame) return;
      try {
        const r = await api(
          `/public/games/${encodeURIComponent(activeGame)}/time`
        );
        if (!r || cancelled) return;
        setAdminStatus(r.status);
        offsetRef.current = new Date(r.server_now).getTime() - Date.now();
        endsAtRef.current = r.ends_at ? new Date(r.ends_at).getTime() : null;
      } catch {
        /* Clock sync is best effort; the socket still pushes start/end. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeGame]);

  async function startGameNow(game = activeGame) {
    if (!game) return showError(t("adminToasts.selectGame"));
    try {
      await api(
        `/admin/games/${encodeURIComponent(game)}/start`,
        { method: "POST" },
        true
      );
      showSuccess(t("adminToasts.gameStarted"));
      await loadGames();
      await syncTime(); // force timer resync
    } catch (e) {
      showError(apiErrorMessage(e, t));
    }
  }

  async function extendGame(game = activeGame, seconds = extendSec) {
    if (!game) return showError(t("adminToasts.selectGame"));
    try {
      await api(
        `/admin/games/${encodeURIComponent(game)}/extend`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ add_seconds: seconds }),
        },
        true
      );
      showSuccess(t("adminToasts.extendedBy", { seconds }));
      await loadGames();
      await syncTime(); // force timer resync
    } catch (e) {
      showError(apiErrorMessage(e, t));
    }
  }

  /* Ending is irreversible (an ended game can be neither restarted nor
     extended), so it goes through a confirmation. */
  function confirmEndGame(game = activeGame) {
    if (!game) return showError(t("adminToasts.selectGame"));
    confirmDialog({
      header: t("admin.endConfirmTitle", { code: game }),
      message: t("admin.endConfirmBody"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: t("admin.endConfirmAccept"),
      rejectLabel: t("actions.cancel"),
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-text",
      /* Enter should not end a game by accident. */
      defaultFocus: "reject",
      accept: () => endGame(game),
    });
  }

  function confirmDeleteGame(game = activeGame) {
    if (!game) return showError(t("adminToasts.selectGame"));
    const live = games.find((g) => g.code === game)?.status === "active";
    confirmDialog({
      header: t("admin.deleteConfirmTitle", { code: game }),
      message: t(live ? "admin.deleteConfirmBodyLive" : "admin.deleteConfirmBody"),
      icon: "pi pi-trash",
      acceptLabel: t("admin.deleteConfirmAccept"),
      rejectLabel: t("actions.cancel"),
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-text",
      defaultFocus: "reject",
      accept: () => deleteGame(game),
    });
  }

  async function deleteGame(game: string) {
    try {
      await api(
        `/admin/games/${encodeURIComponent(game)}`,
        { method: "DELETE" },
        true
      );
      showSuccess(t("adminToasts.gameDeleted", { code: game }));
      /* Clear what belonged to the deleted game before loadGames moves the
         selection, so its teams and scores never linger under another name. */
      setTeams([]);
      setScores([]);
      await loadGames();
    } catch (e) {
      showError(apiErrorMessage(e, t));
    }
  }

  async function endGame(game = activeGame) {
    if (!game) return showError(t("adminToasts.selectGame"));
    try {
      await api(
        `/admin/games/${encodeURIComponent(game)}/end`,
        { method: "POST" },
        true
      );
      showSuccess(t("adminToasts.gameEnded"));
      await loadGames();
      await syncTime(); // force timer resync
    } catch (e) {
      showError(apiErrorMessage(e, t));
    }
  }

  async function loadGames() {
    try {
      const data = await api(`/admin/games`, {}, true);
      setGames(data.games ?? []);
      const exists = (data.games ?? []).some(
        (g: GameRow) => g.code === activeGame
      );
      if (!exists) setActiveGame(data.games?.length ? data.games[0].code : "");
    } catch (e) {
      showError(apiErrorMessage(e, t));
    }
  }
  // createGame
  async function createGame() {
    if (!newCode.trim()) return showError(t("adminToasts.gameCodeError"));
    if (selected.length === 0)
      return showError(t("adminToasts.challengeError"));
    try {
      setLoading(true);
      const data = await api(
        `/admin/games`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code: newCode.trim(),
            max_team_size: newMaxSize,
            status: "lobby",
            challenge_ids: selected.map((c) => c.id),
            duration_seconds: newDurationMin * 60,
          }),
        },
        true
      );

      showSuccess(
        t("adminToasts.gameCreated", {
          code: data.game.code,
          count: selected.length,
        }) //Admin Success Toast
      );
      setNewCode("");
      setNewMaxSize(4);
      setSelected([]);
      setAvailable(allChallenges);

      await loadGames();
      setActiveGame(data.game.code);
      await refresh(data.game.code, { silent: true });
      await loadScores(data.game.code, { silent: true });
    } catch (e) {
      showError(apiErrorMessage(e, t));
    } finally {
      setLoading(false);
    }
  }

  // loadGameChallenges
  async function loadGameChallenges(game = activeGame) {
    if (!game) return;
    try {
      const data = await api(
        `/admin/games/${encodeURIComponent(game)}/challenges`,
        {},
        true
      );
      const chosenIds = new Set(
        (data.challenges ?? []).map(
          (c: { challenge_id: string }) => c.challenge_id
        )
      );
      const chosen = allChallenges.filter((c) => chosenIds.has(c.id));
      const others = allChallenges.filter((c) => !chosenIds.has(c.id));
      setSelected(pinWelcomeFirst(chosen));
      setAvailable(pinWelcomeFirst(others));
    } catch {
      /* Leaves the picker as it was rather than blanking the selection. */
    }
  }

  /* The welcome challenge gates every other one, so it stays first in both
     lists whatever the admin does. This is enforced in state rather than only
     on screen: Selected order is saved as the order players see, and a
     display-only pin would save a different order from the one shown. */
  function moveFromSource(item: Challenge) {
    setAvailable((src) => src.filter((i) => i.id !== item.id));
    setSelected((dst) => pinWelcomeFirst([...dst, item])); // append to end
  }
  function moveFromTarget(item: Challenge) {
    setSelected((dst) => dst.filter((i) => i.id !== item.id));
    setAvailable((src) => pinWelcomeFirst([...src, item]));
  }
  function selectAllAvailable(items: Challenge[]) {
    const ids = new Set(items.map((i) => i.id));
    setAvailable((src) => src.filter((i) => !ids.has(i.id)));
    setSelected((dst) => pinWelcomeFirst([...dst, ...items]));
  }
  function clearAllSelected() {
    setAvailable((src) => pinWelcomeFirst([...src, ...selected]));
    setSelected([]);
  }
  /** Order within Selected is the order players see, so it is draggable. */
  function reorderSelected(fromId: string, toIndex: number) {
    setSelected((list) => {
      const from = list.findIndex((i) => i.id === fromId);
      if (from === -1) return list;
      const next = [...list];
      const [moved] = next.splice(from, 1);
      /* Removing the item first shifts every later index down by one. */
      next.splice(from < toIndex ? toIndex - 1 : toIndex, 0, moved);
      /* Dropping something above the welcome challenge lands it second. */
      return pinWelcomeFirst(next);
    });
  }


  function showToastOnce(opts: {
    severity: "success" | "info" | "warn" | "error";
    summary?: string;
    detail: string;
    life?: number;
    dedupMs?: number; // suppress identical message within this window
  }) {
    const {
      severity,
      summary = "",
      detail,
      life = 2500,
      dedupMs = 5000,
    } = opts;
    const key = `${severity}|${summary}|${detail}`;
    const now = Date.now();

    const until = activeToastsRef.current.get(key);
    if (until && now < until) return; // identical message already shown recently

    // record suppression window
    activeToastsRef.current.set(key, now + dedupMs);

    // show without clearing others (so different messages can co-exist)
    toast.current?.show({ severity, summary, detail, life, closable: true });

    // cleanup after dedup window
    window.setTimeout(() => {
      const v = activeToastsRef.current.get(key);
      if (v && v <= Date.now()) activeToastsRef.current.delete(key);
    }, Math.max(life, dedupMs) + 50);
  }
  useEffect(() => {
    if (activeGame && allChallenges.length) loadGameChallenges(activeGame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGame, allChallenges]);

  // loadAllChallenges
  async function loadAllChallenges() {
    try {
      const langForApi = i18n.language || "en"; // match Play.tsx behaviour

      const data = await api(
        `/admin/challenges?lang=${encodeURIComponent(langForApi)}`,
        {},
        true
      );

      const rows: AdminChallengeDto[] = data.challenges ?? [];

      const mapped: Challenge[] = rows.map((r) => {
        const c = r.content ?? {}; // if backend sends the same JSON content
        return {
          id: r.id,
          key: r.key,
          type: r.type,
          difficulty: r.difficulty,
          points: r.points,
          order_index: r.order_index,
          // when lang=ar this should already be Arabic category from challenges_ar
          category: r.category ?? c.category ?? "Uncategorized",
          title: c.title ?? r.key, // Arabic/English title from mapping table
          content: c,
        };
      });
      setAllChallenges(mapped);
      setAvailable(pinWelcomeFirst(mapped));
      setSelected([]);
    } catch (e) {
      showError(apiErrorMessage(e, t));
    }
  }

  // generate
  async function generate(game = activeGame) {
    if (!game) return showError(t("adminToasts.selectGame"));
    try {
      setLoading(true);
      const data = await api(
        `/admin/games/${encodeURIComponent(game)}/teams`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ count }),
        },
        true
      );
      setTeams(data?.teams ?? []);
      origNamesRef.current = new Map(
        (data?.teams ?? []).map((t: TeamRow) => [t.id, t.name])
      );

      showSuccess(
        t("adminToasts.generatedTeams", {
          count: data?.count ?? count,
        })
      );
    } catch (e) {
      showError(apiErrorMessage(e, t));
    } finally {
      setLoading(false);
    }
  }

  // loadScores
  async function loadScores(
    game = activeGame,
    opts: { silent?: boolean } = {}
  ) {
    if (!game) return showError(t("adminToasts.selectGame"));
    const { silent = false } = opts;
    try {
      setScoreLoading(true);
      const data = await api(
        `/admin/games/${encodeURIComponent(game)}/scoreboard`,
        {},
        true
      );
      setScores(data.scoreboard ?? []);
      if (!silent) showSuccess(t("adminToasts.scoresRefreshed"));
    } catch (e) {
      showError(apiErrorMessage(e, t));
    } finally {
      setScoreLoading(false);
    }
  }

  // refresh
  /**
   * Pull the roster without discarding work in progress.
   *
   * The team name cells are editable, so a plain overwrite would wipe whatever
   * an organiser was halfway through typing the moment a player joined. Rows
   * with unsaved edits keep the local name; everything else (new players,
   * added or removed teams, join links) comes from the server.
   */
  async function refreshTeamsLive(game = activeGame) {
    if (!game) return;
    try {
      const data = await api(
        `/admin/games/${encodeURIComponent(game)}/teams`,
        {},
        true
      );
      const incoming: TeamRow[] = data.teams ?? [];

      setTeams((prev) => {
        const localById = new Map(prev.map((tm) => [tm.id, tm]));
        return incoming.map((next) => {
          const local = localById.get(next.id);
          const edited =
            local && (origNamesRef.current.get(next.id) ?? "") !== local.name;
          return edited ? { ...next, name: local!.name } : next;
        });
      });

      /* Baseline is always the server's value, so the dirty check stays
         honest even for a row we just preserved. */
      origNamesRef.current = new Map(
        incoming.map((tm: TeamRow) => [tm.id, tm.name])
      );
    } catch {
      /* A failed background refresh should not interrupt the organiser. */
    }
  }

  async function refresh(game = activeGame, opts: { silent?: boolean } = {}) {
    if (!game) return showError(t("adminToasts.selectGame"));
    const { silent = false } = opts;
    try {
      setLoading(true);
      const data = await api(
        `/admin/games/${encodeURIComponent(game)}/teams`,
        {},
        true
      );
      setTeams(data.teams ?? []);
      origNamesRef.current = new Map(
        (data.teams ?? []).map((t: TeamRow) => [t.id, t.name])
      );

      if (!silent) showSuccess(t("adminToasts.teamsRefreshed"));
    } catch (e) {
      showError(apiErrorMessage(e, t));
    } finally {
      setLoading(false);
    }
  }

  const origNamesRef = useRef<Map<string, string>>(new Map());
  const [savingId, setSavingId] = useState<string | null>(null);

  async function updateTeamName(
    teamId: string,
    name: string,
    game = activeGame
  ) {
    const data = await api(
      `/admin/games/${encodeURIComponent(game)}/teams/${encodeURIComponent(
        teamId
      )}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      },
      true
    );

    setTeams((prev) =>
      prev.map((t) => (t.id === teamId ? { ...t, name: data.team.name } : t))
    );
    return data;
  }

  function isDirty(row: TeamRow) {
    return (origNamesRef.current.get(row.id) ?? "") !== row.name;
  }

  async function saveRow(row: TeamRow) {
    const newName = row.name?.trim();
    if (!newName) return showError(t("adminToasts.teamEmpty"));
    if (!isDirty(row)) return;

    try {
      setSavingId(row.id);
      await updateTeamName(row.id, newName);

      origNamesRef.current.set(row.id, newName);
      showSuccess(t("adminToasts.savedTeamName"));
    } catch (e) {
      const orig = origNamesRef.current.get(row.id) ?? row.name;
      setTeams((prev) =>
        prev.map((t) => (t.id === row.id ? { ...t, name: orig } : t))
      );
      showError(apiErrorMessage(e, t));
    } finally {
      setSavingId(null);
    }
  }

  /**
   * The API returns the join link prefixed with PUBLIC_BASE_URL, which is
   * empty unless it is configured: leaving a bare "/join/CODE" that is
   * useless once pasted into a chat. Resolve it against the current origin
   * when it is not already absolute.
   */
  function absoluteJoinLink(link: string) {
    if (!link) return link;
    if (/^https?:\/\//i.test(link)) return link;
    return new URL(link, window.location.origin).toString();
  }

  function copy(text: string) {
    navigator.clipboard.writeText(text);
    showSuccess(t("adminToasts.linkCopy"));
  }

  function openScoreboard() {
    if (!activeGame) {
      showError(t("adminToasts.selectGame"));
      return;
    }
    window.open(
      `/scoreboard/${encodeURIComponent(activeGame)}`,
      "_blank",
      "noopener"
    );
  }

  const syncTime = useCallback(async () => {
    if (!activeGame) return;
    try {
      // add a tiny cache-buster just in case
      const r = await api(
        `/public/games/${encodeURIComponent(activeGame)}/time?_=${Date.now()}`
      );
      if (!r) return;

      const serverNow = new Date(r.server_now).getTime();
      offsetRef.current = serverNow - Date.now();
      endsAtRef.current = r.ends_at ? new Date(r.ends_at).getTime() : null;

      /* No interval here: <Countdown> reads these refs on its own tick.
         This used to start a second 500ms timer alongside the one above, so
         the whole dashboard re-rendered four times a second. */
    } catch {
      /* Best effort: a failed resync leaves the previous end time in place. */
    }
  }, [activeGame]);
  const playersRowTemplate = (row: TeamRow) => {
    const players = row.players ?? [];

    if (!players.length) {
      return (
        <div className="players-row">{t("admin.noPlayersYet")}</div>
      );
    }

    return (
      <div className="players-row">
        {players.map((p) => (
          <span key={p.id} className="players-row__chip">
            {p.name}
          </span>
        ))}
      </div>
    );
  };
  const teamNameBody = (row: TeamRow) => (
    <InputText
      value={row.name}
      className="w-full"
      onChange={(e) => {
        const val = e.target.value;
        setTeams((prev) =>
          prev.map((t) => (t.id === row.id ? { ...t, name: val } : t))
        );
      }}
    />
  );

  useEffect(() => {
    loadGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadAllChallenges();
    // if you want selected to follow language changes based on ids,
    // you can also reload game challenges here:
    if (activeGame) {
      loadGameChallenges(activeGame);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    if (selectedGame?.status) {
      setAdminStatus(selectedGame.status);
    }
  }, [selectedGame?.status]);

  useEffect(() => {
    if (!activeGame) return;
    /* revision changes when the server pushes a scoreboard update. */
    loadScores(activeGame, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGame, revision]);

  useEffect(() => {
    if (!activeGame) return;
    /* teamsRevision changes when a player joins or the roster is edited. */
    refreshTeamsLive(activeGame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGame, teamsRevision]);
  useEffect(() => {
    if (activeGame) {
      refresh(activeGame, { silent: true }); // will toast "Teams refreshed." once; de-duped
      loadScores(activeGame, { silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGame]);
  useEffect(() => {
    syncTime();
    return () => {
      /* intervalRef holds a timer id, not a DOM node, and syncTime replaces it
         on every run, so reading it at cleanup time is exactly what we want. */
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [syncTime]);
  // table cell renderers
  /* Only Save commits anything, so it is the one solid button: and only once
     the row is actually dirty. Three filled buttons per row turned the table
     into a wall of blue. */
  const actionsBody = (row: TeamRow) => (
    <div className="flex gap-2 justify-content-end">
      <Button
        label={t("admin.open")}
        icon="pi pi-external-link"
        size="small"
        severity="secondary"
        text
        onClick={() =>
          window.open(absoluteJoinLink(row.join_link), "_blank", "noopener")
        }
      />
      <Button
        label={t("admin.copy")}
        icon="pi pi-copy"
        size="small"
        severity="secondary"
        text
        onClick={() => copy(absoluteJoinLink(row.join_link))}
      />
      <Button
        label={t("admin.save")}
        icon="pi pi-check"
        size="small"
        severity="success"
        text={!isDirty(row)}
        onClick={() => saveRow(row)}
        loading={savingId === row.id}
        disabled={!isDirty(row)}
      />
    </div>
  );

  const linkBody = (row: TeamRow) => (
    <div className="p-inputgroup" dir="ltr">
      <InputText readOnly value={absoluteJoinLink(row.join_link)} />
      <Button
        icon="pi pi-copy"
        severity="secondary"
        aria-label={t("admin.copy")}
        onClick={() => copy(absoluteJoinLink(row.join_link))}
      />
    </div>
  );
  const teamColumnBody = (row: TeamTableRow) =>
    row.rowType === "team" ? (
      <div style={{ paddingTop: "0.2rem", paddingBottom: "0.2rem" }}>
        {teamNameBody(row.team)}
      </div>
    ) : (
      playersRowTemplate(row.team)
    );

  const joinLinkColumnBody = (row: TeamTableRow) =>
    row.rowType === "team" ? linkBody(row.team) : null;

  const actionsColumnBody = (row: TeamTableRow) =>
    row.rowType === "team" ? actionsBody(row.team) : null;

  /* Every hook above has run, so bailing out here keeps the hook order
     stable. An expired token sends the organiser back to the login form. */
  if (!tokenOk) return null;

  const headerActions = (
    <>
      <Button
        label={t("admin.viewScoreboard")}
        icon="pi pi-chart-bar"
        className="p-button-sm"
        onClick={openScoreboard}
        disabled={!activeGame}
      />
      <Button
        label={t("admin.logout")}
        icon="pi pi-sign-out"
        severity="danger"
        outlined
        className="p-button-sm"
        onClick={onLogout}
      />
    </>
  );

  return (
    <AppShell
      actions={headerActions}
      homeHref="/admin"
      tagline={activeGame || undefined}
    >
      <AppToast ref={toast} position="top-right" />
      <ConfirmDialog />

      <div className="section-head" style={{ marginTop: 0 }}>
        <h1 className="section-head__title">{t("admin.admindashboard")}</h1>
      </div>

      {/* ================= Create game ================= */}
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">{t("admin.createGame")}</h2>
          </div>
        </div>

        <div className="form-grid">
          <div>
            <label className="field__label" htmlFor="new-code">
              {t("admin.code")}
            </label>
            <InputText
              id="new-code"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              placeholder={t("admin.createplholder")}
              className="w-full force-ltr"
              aria-describedby="new-code-hint"
            />
            {/* The server rejects anything else with a 400; saying so up front
                is friendlier than letting them find out on submit. */}
            <p className="field__hint" id="new-code-hint">
              {t("admin.codeHint")}
            </p>
          </div>

          <div>
            <label className="field__label" htmlFor="new-size">
              {t("admin.maxteamsize")}
            </label>
            <InputNumber
              inputId="new-size"
              value={newMaxSize}
              onValueChange={(e) => setNewMaxSize(e.value ?? 1)}
              min={1}
              max={20}
              showButtons
              className="w-full"
              inputClassName="w-full force-ltr"
            />
          </div>

          <div>
            <label className="field__label" htmlFor="new-duration">
              {t("admin.duration")}
            </label>
            <InputNumber
              inputId="new-duration"
              value={newDurationMin}
              onValueChange={(e) => setNewDurationMin(e.value ?? 1)}
              min={1}
              max={24 * 60}
              showButtons
              className="w-full"
              inputClassName="w-full force-ltr"
            />
          </div>

        </div>

        {/* Own subsection: without the rule above it, the category filter
            reads as a fifth field in the Create Game row. */}
        <section className="subsection">
          <div className="subsection__head">
            <div>
              <h3 className="subsection__title">{t("admin.selectTxt")}</h3>
              <p className="panel__sub">
                {t("admin.showing")}{" "}
                {categoryFilter === "__all__"
                  ? t("admin.allcategories")
                  : categoryFilter}
                {" · "}
                {t("admin.available2")} {filteredAvailable.length}
              </p>
            </div>

            <Dropdown
              className="w-14rem"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.value)}
              aria-label={t("admin.allcategories")}
              options={[
                { label: t("admin.allcategories"), value: "__all__" },
                ...categories.map((c) => ({ label: c, value: c })),
              ]}
            />
          </div>

          <ChallengePicker
            available={filteredAvailable}
            selected={selected}
            onMoveToSelected={moveFromSource}
            onMoveToAvailable={moveFromTarget}
            onReorderSelected={reorderSelected}
            onSelectAll={selectAllAvailable}
            onClearAll={clearAllSelected}
            isPinned={isWelcomeChallenge}
            pinnedHint={t("admin.pinnedFirst")}
          />
        </section>

        <div style={{ marginTop: "var(--space-5)" }}>
          <Button
            label={t("admin.create")}
            icon="pi pi-plus"
            onClick={createGame}
            loading={loading}
          />
        </div>
      </section>

      {/* ================= Teams ================= */}
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">{t("admin.teams")}</h2>
            <p className="panel__sub">{t("admin.teamsTxt")}</p>
          </div>
        </div>

        <div className="control-row" style={{ marginBottom: "var(--space-5)" }}>
          <div className="control-cell" style={{ width: "9rem" }}>
            <label className="field__label" htmlFor="team-count">
              {t("admin.numOfTeams")}
            </label>
            <InputNumber
              inputId="team-count"
              value={count}
              onValueChange={(e) => setCount(e.value ?? 0)}
              min={1}
              max={100}
              showButtons
              disabled={!activeGame}
              inputClassName="w-full force-ltr"
            />
          </div>

          <Button
            label={t("admin.genReset")}
            icon="pi pi-refresh"
            onClick={() => generate()}
            loading={loading}
            disabled={!activeGame}
          />
          <Button
            label={t("admin.refresh")}
            icon="pi pi-sync"
            severity="secondary"
            outlined
            onClick={() => refresh(activeGame, { silent: false })}
            loading={loading}
            disabled={!activeGame}
          />
        </div>

        <DataTable
          className="teams-table"
          key={i18n.language}
          value={tableRows}
          stripedRows
          loading={loading}
          emptyMessage={
            <div style={{ padding: "var(--space-4) 0" }}>
              {activeGame ? t("admin.teamTxt") : t("admin.selectTeamTxt")}
            </div>
          }
          scrollable
          scrollHeight="400px"
          dataKey="rowId"
          rowClassName={(row: TeamTableRow) => ({
            "players-row-tr": row.rowType === "players",
          })}
        >
          <Column
            header={t("admin.team")}
            body={teamColumnBody}
            style={{ minWidth: "16rem" }}
          />
          <Column
            header={t("admin.joinLink")}
            body={joinLinkColumnBody}
            style={{ minWidth: "18rem" }}
          />
          <Column
            header={t("admin.action")}
            body={actionsColumnBody}
            style={{ minWidth: "15rem" }}
          />
        </DataTable>
      </section>

      {/* ================= Game control ================= */}
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">{t("admin.gameControl")}</h2>
            <p className="panel__sub">{t("admin.gameControlTxt")}</p>
          </div>
        </div>

        <div className="gamebar">
          <div className="control-cell" style={{ width: "17rem" }}>
            <label className="field__label" htmlFor="active-game">
              {t("admin.activeGame")}
            </label>
            <Dropdown
              inputId="active-game"
              value={activeGame}
              onChange={(e) => setActiveGame(e.value)}
              className="w-full"
              options={games.map((g) => ({
                label: `${g.code} (${t(`admin.${g.status}`)})`,
                value: g.code,
              }))}
              placeholder={
                games.length ? t("admin.selectGame") : t("admin.noGames")
              }
            />
          </div>

          {/* Status and timer belong together: grouping them into one block
              stops the row reading as four unrelated fields. */}
          <div className="gamebar__state">
            <div className="gamebar__stat">
              <span className="field__label">{t("admin.status")}</span>
              <StatusPill status={adminStatus} />
            </div>
            <div className="gamebar__divider" aria-hidden="true" />
            <div className="gamebar__stat">
              <span className="field__label">{t("admin.timer")}</span>
              <Countdown getRemaining={getRemaining} className="timer" />
            </div>
          </div>

          <div className="gamebar__actions">
            {canStart && (
              <Button
                label={t("admin.start")}
                icon="pi pi-play"
                severity="success"
                onClick={() => startGameNow()}
              />
            )}

            {canExtend && (
              <>
                <div className="control-cell" style={{ width: "11.5rem" }}>
                  <label className="field__label" htmlFor="extend-min">
                    {t("admin.extend")}
                  </label>
                  <div className="p-inputgroup" dir="ltr">
                    <InputNumber
                      inputId="extend-min"
                      value={extendSec}
                      onValueChange={(e) => setExtendSec(e.value ?? 60)}
                      min={1}
                      max={3600}
                      inputClassName="w-full"
                    />
                    {/* Field is minutes; the API takes seconds. */}
                    <Button
                      icon="pi pi-plus"
                      aria-label={t("admin.extend")}
                      onClick={() => extendGame(activeGame, extendSec * 60)}
                    />
                  </div>
                </div>
                <Button
                  label={t("admin.add1Minute")}
                  severity="secondary"
                  outlined
                  onClick={() => extendGame(activeGame, 60)}
                />
                <Button
                  label={t("admin.add5Minutes")}
                  severity="secondary"
                  outlined
                  onClick={() => extendGame(activeGame, 300)}
                />
              </>
            )}

            {selectedGame && selectedGame.status !== "ended" && (
              <Button
                label={t("admin.end")}
                icon="pi pi-power-off"
                severity="danger"
                onClick={() => confirmEndGame()}
              />
            )}

            {/* Outlined so the one solid danger button stays End: deleting is
                rarer, and it sits last so it is never the obvious next click. */}
            {selectedGame && (
              <Button
                label={t("admin.deleteGame")}
                icon="pi pi-trash"
                severity="danger"
                outlined
                onClick={() => confirmDeleteGame()}
              />
            )}
          </div>
        </div>

        {selectedGame?.status === "lobby" && (
          <p className="field__hint" style={{ marginTop: "var(--space-3)" }}>
            {t("admin.timerTxt")}
          </p>
        )}
      </section>

      {/* ================= Scoreboard ================= */}
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">{t("scoreboard.livescoreboard")}</h2>
            <p className="panel__sub">
              {t("scoreboard.livept")}
              {" · "}
              {liveConnected ? t("ui.live") : t("ui.reconnecting")}
            </p>
          </div>

          <div className="panel__actions">
            <Button
              label={t("scoreboard.refresh")}
              icon="pi pi-chart-bar"
              className="p-button-sm"
              onClick={() => loadScores(activeGame, { silent: false })}
              loading={scoreLoading}
              disabled={!activeGame}
            />
          </div>
        </div>

        <DataTable
          key={i18n.language}
          value={scores}
          loading={scoreLoading}
          rowHover
          stripedRows
          emptyMessage={
            activeGame ? t("admin.noscore") : t("admin.selectTeamTxt")
          }
          scrollable
          scrollHeight="400px"
        >
          <Column
            field="rank"
            header="#"
            body={(r: ScoreRow) => <span className="mono">{r.rank}</span>}
            style={{ width: "4.5rem", textAlign: "center" }}
          />
          <Column field="team_name" header={t("scoreboard.team")} />
          <Column
            field="points"
            header={t("scoreboard.points")}
            sortable
            body={(r: ScoreRow) => (
              <span className="mono">{Number(r.points ?? 0).toLocaleString()}</span>
            )}
            style={{ width: "8rem" }}
          />
          <Column
            field="solves"
            header={t("scoreboard.solves")}
            sortable
            body={(r: ScoreRow) => <span className="mono">{r.solves ?? 0}</span>}
            style={{ width: "7rem" }}
          />
          <Column
            field="last_solve"
            header={t("scoreboard.lastsolve")}
            body={(r: ScoreRow) =>
              r.last_solve ? (
                  <span className="force-ltr">
                    {new Date(r.last_solve).toLocaleString()}
                  </span>
                ) : (
                  "-"
                )
            }
            style={{ width: "15rem" }}
          />
        </DataTable>
      </section>
    </AppShell>
  );
}
